import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import { I18nextProvider } from 'react-i18next';
import type { i18n } from 'i18next';
import type { SqlDriver } from '../data/db/driver';
import type { Store } from '../data/store/store';
import type { DeviceIdentity } from '../auth/auth';
import { setMeta } from '../data/appMeta';
import type { BudgetSpace, BudgetType } from '../domain/model';
import type { Cloud } from '../sync/cloud';
import type { EntitlementSource, Storefront } from '../premium/entitlement';
import type { AdsBridge } from '../ads/ads';
import type { Language } from '../i18n/i18n';
import { DEFAULT_REGION, findRegion } from '../i18n/regions';
import { FormatProvider } from '../ui/format';

/**
 * Everything the UI needs from the outside world, created once at startup
 * (bootstrap.ts) and injected here. Tests build the same object around an
 * in-memory database, so screens never reach for globals.
 */
export type ThemePreference = 'system' | 'light' | 'dark';

export interface AppServices {
  db: SqlDriver;
  /** Repositories over the local database (the only way screens touch data). */
  store: Store;
  identity: DeviceIdentity;
  i18n: i18n;
  platform: 'ios' | 'android' | 'web';
  schemaVersion: number;
  theme: ThemePreference;
  /** The budget on screen; null until onboarding has created one. */
  activeSpace: BudgetSpace | null;
  /** Home shows made-up numbers (design preview; nothing is stored). */
  sampleData: boolean;
  /** Account + sync; null when this build has no cloud configured (everything stays on the device). */
  cloud: Cloud | null;
  /** Whether this person has Costline Premium (see premium/entitlement.ts). */
  premium: EntitlementSource;
  /** Buying Premium: the App Store / Google Play; null on the web (and in builds without a store key). */
  storefront: Storefront | null;
  /** The free app's banner on phones; null on the web and in builds without an ad unit. */
  ads: AdsBridge | null;
  /** Start the app again from the database (after signing in adopts another identity). */
  restart(): void;
}

export interface OnboardingResult {
  country: string;
  currency: string;
  language: Language;
  type: BudgetType;
  name: string;
  monthlyTargetMinor: number | null;
}

interface ServicesValue extends AppServices {
  language: Language;
  setLanguage(language: Language): Promise<void>;
  setTheme(theme: ThemePreference): Promise<void>;
  completeOnboarding(result: OnboardingResult): Promise<BudgetSpace>;
  /** A further budget for the same user (its own members, categories, currency, entries); becomes the active one. */
  createSpace(input: NewBudgetInput): Promise<BudgetSpace>;
  /** Rename or retype a budget; the active one is refreshed in place. */
  updateSpace(id: string, patch: Partial<Pick<BudgetSpace, 'name' | 'type' | 'monthlyTargetMinor'>>): Promise<BudgetSpace>;
  setActiveSpace(space: BudgetSpace): Promise<void>;
  setSampleData(on: boolean): Promise<void>;
  /** Bumped after every write so screens that read the store refresh. */
  dataVersion: number;
  notifyDataChanged(): void;
}

const Ctx = createContext<ServicesValue | null>(null);

export const LANGUAGE_KEY = 'settings.language';
export const THEME_KEY = 'settings.theme';
export const ACTIVE_SPACE_KEY = 'settings.activeSpaceId';

export interface NewBudgetInput {
  name: string;
  type: BudgetType;
  currency: string;
  monthlyTargetMinor?: number | null;
}
export const SAMPLE_DATA_KEY = 'preview.sampleData';

/** Placeholder member name until a profile step exists. */
export const OWNER_DISPLAY_NAME = 'Me';

export function isThemePreference(v: string | undefined): v is ThemePreference {
  return v === 'system' || v === 'light' || v === 'dark';
}

/** `system` follows the OS; light/dark pin the tokens via data-theme. */
export function applyTheme(theme: ThemePreference) {
  if (typeof document === 'undefined') return;
  if (theme === 'system') delete document.documentElement.dataset.theme;
  else document.documentElement.dataset.theme = theme;
}

/** Billing region + budget currency drive every number on screen. */
export function formatFor(space: BudgetSpace | null) {
  const region = (space?.billingCountry && findRegion(space.billingCountry)) || DEFAULT_REGION;
  return { formatLocale: region.formatLocale, currency: space?.currency ?? region.currency };
}

export function ServicesProvider({ services, children }: { services: AppServices; children: ReactNode }) {
  const [language, setLang] = useState(services.i18n.language as Language);
  const [theme, setThemeState] = useState(services.theme);
  const [activeSpace, setActive] = useState(services.activeSpace);
  const [sampleData, setSample] = useState(services.sampleData);
  const [dataVersion, setDataVersion] = useState(0);

  const value = useMemo<ServicesValue>(() => {
    const setLanguage = async (next: Language) => {
      await services.i18n.changeLanguage(next);
      await setMeta(services.db, LANGUAGE_KEY, next);
      document.documentElement.lang = next;
      setLang(next);
    };
    const setActiveSpace = async (space: BudgetSpace) => {
      await setMeta(services.db, ACTIVE_SPACE_KEY, space.id);
      setActive(space);
    };
    return {
      ...services,
      language,
      theme,
      activeSpace,
      sampleData,
      dataVersion,
      notifyDataChanged: () => setDataVersion((v) => v + 1),
      async setSampleData(on) {
        setSample(on);
        await setMeta(services.db, SAMPLE_DATA_KEY, on ? '1' : '0');
      },
      setLanguage,
      setActiveSpace,
      async setTheme(next) {
        applyTheme(next);
        setThemeState(next);
        await setMeta(services.db, THEME_KEY, next);
      },
      async createSpace(input) {
        const { space } = await services.store.spaces.createWithOwner({
          name: input.name,
          type: input.type,
          currency: input.currency,
          // Same bills country as the budget you are in: number and date formats stay familiar.
          billingCountry: activeSpace?.billingCountry ?? null,
          monthlyTargetMinor: input.monthlyTargetMinor ?? null,
          ownerUserId: services.identity.session.userId,
          ownerDisplayName: OWNER_DISPLAY_NAME,
        });
        await setActiveSpace(space);
        return space;
      },
      async updateSpace(id, patch) {
        const next = await services.store.spaces.update(id, patch);
        if (activeSpace?.id === id) setActive(next);
        setDataVersion((v) => v + 1);
        return next;
      },
      async completeOnboarding(r) {
        const { space } = await services.store.spaces.createWithOwner({
          name: r.name,
          type: r.type,
          currency: r.currency,
          billingCountry: r.country,
          monthlyTargetMinor: r.monthlyTargetMinor,
          ownerUserId: services.identity.session.userId,
          ownerDisplayName: OWNER_DISPLAY_NAME,
        });
        if (r.language !== language) await setLanguage(r.language);
        await setActiveSpace(space);
        return space;
      },
    };
  }, [services, language, theme, activeSpace, sampleData, dataVersion]);

  const format = useMemo(() => formatFor(activeSpace), [activeSpace]);
  return (
    <Ctx.Provider value={value}>
      <I18nextProvider i18n={services.i18n}>
        <FormatProvider value={format}>{children}</FormatProvider>
      </I18nextProvider>
    </Ctx.Provider>
  );
}

export function useServices(): ServicesValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useServices outside ServicesProvider');
  return v;
}
